import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, computed, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, tap } from 'rxjs';

import { API_CONFIG } from '../config/api.config';
import { AuthUser, TokenResponse } from '../models/auth.model';
import { TokenService } from './token.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly userSignal = signal<AuthUser | null>(null);

  /** Expose current user as a readonly signal */
  readonly currentUser = this.userSignal.asReadonly();

  /** Computed convenience — true when a valid (non-expired) token exists */
  readonly isAuthenticated = computed(() => {
    return !!this.userSignal() && !this.tokenService.isTokenExpired();
  });

  constructor(
    private readonly http: HttpClient,
    private readonly tokenService: TokenService,
    private readonly router: Router,
  ) {
    // Restore user from stored token on service init
    this.restoreSession();
  }

  /**
   * Start the OAuth2 Authorization Code flow.
   * Full-page redirect to the auth server's /oauth2/authorize endpoint.
   *
   * Flow: browser → auth server → (if no session) → redirect to /login (Angular) →
   *       user submits form POST to auth server → session created →
   *       redirect back to /oauth2/authorize → issues code →
   *       redirect to /callback → exchangeCode()
   */
  startAuthFlow(): void {
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: API_CONFIG.clientId,
      redirect_uri: API_CONFIG.redirectUri,
      scope: API_CONFIG.scopes,
    });
    window.location.href = `${API_CONFIG.authServerUrl}/oauth2/authorize?${params.toString()}`;
  }

  /**
   * Exchange an authorization code for JWT tokens.
   * Called from the /callback route after the OAuth2 redirect.
   * Uses the proxy (/oauth2/token → http://localhost:8080/oauth2/token).
   */
  exchangeCode(code: string): Observable<TokenResponse> {
    const body = new URLSearchParams();
    body.set('grant_type', 'authorization_code');
    body.set('code', code);
    body.set('redirect_uri', API_CONFIG.redirectUri);
    body.set('client_id', API_CONFIG.clientId);
    body.set('client_secret', API_CONFIG.clientSecret);

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    return this.http
      .post<TokenResponse>(`${API_CONFIG.authServerUrl}${API_CONFIG.oauth2TokenUrl}`, body.toString(), { headers })
      .pipe(
        tap((response) => {
          this.tokenService.saveTokens(response);
          this.loadUserFromToken();
        }),
      );
  }

  /** Clear tokens and redirect to login */
  logout(): void {
    this.tokenService.clear();
    this.userSignal.set(null);
    this.router.navigate(['/login']);
  }

  /** Try to restore a session from a previously stored JWT */
  private restoreSession(): void {
    if (!this.tokenService.isTokenExpired()) {
      this.loadUserFromToken();
    } else {
      this.tokenService.clear();
    }
  }

  /** Decode the JWT and populate the user signal */
  private loadUserFromToken(): void {
    const payload = this.tokenService.decodeToken();
    if (!payload) {
      this.userSignal.set(null);
      return;
    }

    const user: AuthUser = {
      personId: (payload['person_id'] as number) ?? null,
      email: (payload['email'] as string) ?? payload.sub ?? '',
      fullName: (payload['full_name'] as string) ?? payload.sub ?? '',
      roles: Array.isArray(payload['roles']) ? (payload['roles'] as string[]) : [],
      permissions: Array.isArray(payload['permissions'])
        ? (payload['permissions'] as string[])
        : [],
      scopes: Array.isArray(payload['scope']) ? (payload['scope'] as string[]) : [],
    };

    this.userSignal.set(user);
  }
}
